// Replacement part finder: three dropdowns that narrow, then a result.
//
// Two sources, and the section chooses between them.
//
//   catalog  [data-aps-finder-tree], the compiled shop.aps.finder_tree metafield,
//            nested type -> brand -> [model, collection handle, product count]. Each
//            model therefore lands on a real page with a real count rather than on a
//            search. This is the destination the original note called for: as of R2 the
//            catalogue does carry fitment, on 4,597 products through
//            aps.fits_equipment_type and 57 fits: collections.
//
//            It is a compiled metafield rather than a loop over the aps_finder_row
//            metaobjects because shop.metaobjects.<type>.values is capped at 50 in
//            Liquid: 57 families rendered as 50 and the rest vanished with no error.
//   blocks   The original [data-aps-finder-group] fragments, one per equipment type
//            and brand pairing, hand authored in the theme editor. Kept because the
//            homepage still uses it and because a family with no collection yet has
//            nowhere else to live.
//
// Both build the same tree. A model is {name, url, n} from the catalog and a bare
// string from the blocks, so everything downstream reads model.name.
//
// One function scope, as in aps-search.js. The parts finder section and the nav's finder
// drawer both load this asset, so on the homepage and the parts hub it runs twice. A top
// level class in a classic script is a global binding, and the second run is refused
// before a line of it executes: "Identifier 'ApsFinder' has already been declared". The
// guard on define() at the foot never got the chance to help. The finder worked, since the
// first run had registered it, but every visit to those pages logged a script error, and
// Clarity counted it (found 2026-09-21). Inside a function the second run declares its own
// class and the guard then skips the define.
(() => {

class ApsFinder extends HTMLElement {
  connectedCallback() {
    this.out = this.querySelector('[data-aps-finder-out]');
    // Optional. The drawer carries a call to action instead of a result line, so the count lives
    // on the button. An <a> with no href is not focusable and not clickable, so the idle state is
    // genuinely inert rather than a grey button that still responds.
    this.cta = this.querySelector('[data-aps-finder-cta]');
    this.ctaIdle = this.dataset.ctaIdle || '';

    // The empty option's text. On the page finder the placeholder is the only label a step has, so
    // it doubles as both. The drawer prints the label above the control, so repeating it inside
    // read as "Brand / Brand" the moment the script refilled step two. Optional, and falls back to
    // the placeholder, so the page finder is unchanged.
    this.prompts = [
      this.dataset.promptType || this.dataset.phType,
      this.dataset.promptBrand || this.dataset.phBrand,
      this.dataset.promptModel || this.dataset.phModel,
    ];
    this.selects = [1, 2, 3].map((n) => this.querySelector(`[data-aps-f="${n}"]`));
    this.marks = [...this.querySelectorAll('.rpf-f .num')];
    if (this.selects.some((s) => !s)) return;

    this.mode = this.dataset.mode || 'search';
    this.tree = {};

    // The catalog source. One value, one parse, no cap.
    const treeTag = this.querySelector('[data-aps-finder-tree]');
    if (treeTag) {
      let raw = {};
      try { raw = JSON.parse(treeTag.textContent) || {}; } catch { raw = {}; }
      // The metafield is delivered as a JSON string by `| json` on a json-typed value,
      // so one more parse when the first hands back a string rather than an object.
      if (typeof raw === 'string') { try { raw = JSON.parse(raw); } catch { raw = {}; } }
      for (const [type, brands] of Object.entries(raw)) {
        for (const [brand, models] of Object.entries(brands || {})) {
          const list = (models || [])
            .filter((m) => Array.isArray(m) && m[0] && m[1])
            .map(([name, handle, n]) => ({ name: String(name), url: `/collections/${handle}`, n: Number(n) || 0 }));
          if (list.length) (this.tree[type] ??= {})[brand] = { models: list, terms: '' };
        }
      }
    }

    // One fragment per Finder group block. Parsed individually so a single
    // malformed entry cannot take the whole finder down with it. A block pairing does
    // not overwrite a catalog one; the catalog is the better answer where both exist.
    for (const tag of this.querySelectorAll('[data-aps-finder-group]')) {
      let g;
      try { g = JSON.parse(tag.textContent); } catch { continue; }
      const models = (g.models || []).map((m) => String(m).trim()).filter(Boolean)
        .map((name) => ({ name, url: '', n: 0 }));
      if (!models.length) continue;
      const brands = (this.tree[g.type] ??= {});
      if (brands[g.brand]) continue;
      brands[g.brand] = { models, terms: g.terms || '' };
    }

    const types = Object.keys(this.tree).sort();
    if (!types.length) { this.selects[0].disabled = true; return; }

    this.fill(this.selects[0], types, this.prompts[0]);
    this.selects[0].disabled = false;

    this.selects[0].addEventListener('change', () => this.onType());
    this.selects[1].addEventListener('change', () => this.onBrand());
    this.selects[2].addEventListener('change', () => this.onModel());
    this.paint();
  }

  fill(select, items, placeholder) {
    const opts = [`<option value="">${placeholder}</option>`];
    for (const item of items) {
      // Options come from merchant-entered text, so escape rather than interpolate.
      const o = document.createElement('option');
      o.textContent = typeof item === 'string' ? item : item.name;
      opts.push(o.outerHTML);
    }
    select.innerHTML = opts.join('');
    select.disabled = items.length === 0;
  }

  // The chosen model, as the {name, url, n} record rather than the string in the select.
  model() {
    const type = this.selects[0].value, brand = this.selects[1].value, name = this.selects[2].value;
    if (!type || !brand || !name) return null;
    return (this.tree[type]?.[brand]?.models || []).find((m) => m.name === name) || null;
  }

  reset(select, placeholder) {
    select.innerHTML = `<option value="">${placeholder}</option>`;
    select.disabled = true;
  }

  clear() {
    this.out.className = 'rpf-out';
    this.out.replaceChildren();
    if (this.cta) {
      this.cta.removeAttribute('href');
      this.cta.setAttribute('aria-disabled', 'true');
      this.cta.textContent = this.ctaIdle;
    }
  }

  paint() {
    // Step 1 is always live; the others light up once they carry a choice.
    this.selects.forEach((s, i) => this.marks[i]?.classList.toggle('on', !!s.value || i === 0));
  }

  onType() {
    this.clear();
    this.reset(this.selects[2], this.prompts[2]);
    const type = this.selects[0].value;
    if (!type) { this.reset(this.selects[1], this.prompts[1]); this.paint(); return; }
    this.fill(this.selects[1], Object.keys(this.tree[type]).sort(), this.prompts[1]);
    this.paint();
  }

  onBrand() {
    this.clear();
    const type = this.selects[0].value, brand = this.selects[1].value;
    if (!brand) { this.reset(this.selects[2], this.prompts[2]); this.paint(); return; }
    this.fill(this.selects[2], this.tree[type][brand].models, this.prompts[2]);
    this.selects[2].disabled = false;
    this.paint();
  }

  onModel() {
    this.paint();
    if (!this.selects[2].value) { this.clear(); return; }

    // The button is the whole answer where there is one, so the result line stays empty.
    const m = this.mode === 'collection' ? this.model() : null;
    if (this.cta && m && m.url) {
      this.cta.href = m.url;
      this.cta.removeAttribute('aria-disabled');
      this.cta.textContent = `${this.dataset.resultLinkLabel || 'See the parts'} (${m.n})`;
      this.out.className = 'rpf-out';
      this.out.replaceChildren();
      return;
    }

    this.out.className = 'rpf-out on';
    let node;
    if (this.mode === 'demo') node = this.demoResult();
    else if (this.mode === 'collection') node = this.collectionResult() || this.searchResult();
    else node = this.searchResult();
    this.out.replaceChildren(node);
  }

  result(imageNode, countHtml, linkLabel, href) {
    const res = document.createElement('div');
    res.className = 'pf-res';

    // Only build the image well when there is an image for it. It carries a border
    // and a white fill, so an empty one renders as a stray box beside the text --
    // invisible to a geometry check, obvious on screen.
    let unit = null;
    if (imageNode) {
      unit = document.createElement('span');
      unit.className = 'pf-unit';
      unit.appendChild(imageNode);
    }

    const info = document.createElement('span');
    info.className = 'pf-info';
    const count = document.createElement('span');
    count.className = 'pf-count';
    count.appendChild(countHtml);
    const link = document.createElement('a');
    link.className = 'pf-diag';
    link.textContent = linkLabel;
    link.href = href;
    info.append(count, link);

    res.append(...(unit ? [unit, info] : [info]));
    return res;
  }

  demoResult() {
    const tpl = this.querySelector('[data-aps-demo-image]');
    const img = tpl ? tpl.content.cloneNode(true) : null;
    const frag = document.createDocumentFragment();
    const b = document.createElement('b');
    b.textContent = this.dataset.demoCount;
    frag.append(b, document.createTextNode(' ' + this.dataset.demoSuffix));
    return this.result(img, frag, this.dataset.demoLinkLabel, this.dataset.demoLink);
  }

  // The catalog result. A real page and a real count, which is the whole reason the
  // catalog source exists. Falls back to a search when the chosen model has no
  // collection, which is the case for any pairing that came from a Finder group block.
  collectionResult() {
    const m = this.model();
    if (!m || !m.url) return null;
    const brand = this.selects[1].value;

    const frag = document.createDocumentFragment();
    const b = document.createElement('b');
    b.textContent = `${m.n} ${this.dataset.partsLabel || 'parts'}`;
    frag.append(b, document.createTextNode(` for ${brand} ${m.name}`));

    return this.result(null, frag, this.dataset.resultLinkLabel, m.url);
  }

  searchResult() {
    const type = this.selects[0].value;
    const brand = this.selects[1].value;
    const model = this.selects[2].value;
    const terms = this.tree[type][brand].terms;

    const query = [brand, model, terms].filter(Boolean).join(' ');
    const href = `${this.dataset.searchUrl}?q=${encodeURIComponent(query)}&type=product`;

    // No count. A number here would have to be invented, and an invented count is
    // the one thing this component must never show.
    const frag = document.createDocumentFragment();
    const b = document.createElement('b');
    b.textContent = this.dataset.resultLabel;
    frag.append(b, document.createTextNode(` ${brand} ${model}`));

    return this.result(null, frag, this.dataset.resultLinkLabel, href);
  }
}
// Guarded. Both the parts finder section and the nav's finder drawer load this asset, and on
// /collections/parts both are on the page. A second define for the same tag name throws
// "this name has already been used with this registry" and takes the rest of the file with it.
if (!customElements.get('aps-finder')) customElements.define('aps-finder', ApsFinder);

})();
